import { IconComponent } from "@nova/component-icons"
import classNames from "classnames"
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"

export const SegmentedControl = ({
    segments,
    defaultSelectedIndex = 0,
    zIndex = 0,
    onChange,
    className,
    thumbClassName,
}: {
    segments:
        | (string | IconComponent | ReactNode)[]
        | readonly (string | IconComponent | ReactNode)[]
    defaultSelectedIndex?: number
    zIndex?: number
    onChange: (index: number) => void
    className?: string
    thumbClassName?: string
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const segmentRefs = useRef<(HTMLDivElement | null)[]>([])
    const [thumbStyle, setThumbStyle] = useState({ left: 0, width: 0 })
    const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex)
    const selectedIndexRef = useRef(selectedIndex)
    useEffect(() => {
        if (defaultSelectedIndex !== selectedIndexRef.current) {
            setSelectedIndex(defaultSelectedIndex)
            selectedIndexRef.current = defaultSelectedIndex
        }
    }, [defaultSelectedIndex])

    const updateThumb = useCallback(() => {
        const container = containerRef.current
        const segment = segmentRefs.current[selectedIndex]
        if (!container || !segment) return

        const containerRect = container.getBoundingClientRect()
        const segmentRect = segment.getBoundingClientRect()

        setThumbStyle({
            left: segmentRect.left - containerRect.left,
            width: segmentRect.width,
        })
    }, [selectedIndex])

    useEffect(() => {
        updateThumb()
    }, [segments.length, updateThumb])

    useEffect(() => {
        const obs = new ResizeObserver(updateThumb)
        if (containerRef.current) obs.observe(containerRef.current)
        return () => obs.disconnect()
    }, [updateThumb])

    return (
        <div
            ref={containerRef}
            className={classNames(
                "w-full h-8 bg-gray-700 p-0.5 rounded-lg relative flex items-center select-none",
                className
            )}
        >
            {/* Thumb */}
            <div
                className={classNames(
                    "absolute h-7 bg-gray-500 rounded-md transition-[width,left] duration-300 ease-in-out border-b border-b-white-100 shadow-thumb",
                    thumbClassName
                )}
                style={{
                    left: thumbStyle.left,
                    width: thumbStyle.width,
                    willChange: "left, width",
                }}
            />
            {/* All Options */}
            {segments.map((segment, i) => (
                <div
                    key={i}
                    ref={(el) => {
                        segmentRefs.current[i] = el
                    }}
                    className={classNames(
                        "h-full flex-1 flex items-center justify-center cursor-pointer",
                        {
                            "text-gray-0": selectedIndex === i,
                            "text-gray-300 hover:text-gray-50": selectedIndex !== i,
                        }
                    )}
                    style={{ zIndex: zIndex + 1 }}
                    onClick={() => {
                        setSelectedIndex(i)
                        onChange(i)
                    }}
                >
                    {typeof segment === "string" ? (
                        <small>{segment}</small>
                    ) : typeof segment === "function" ? (
                        <Icons.ExtraSmallIcon Icon={segment} />
                    ) : (
                        <>{segment}</>
                    )}
                </div>
            ))}
        </div>
    )
}
